import {
  format,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
} from 'date-fns'

// ── Class name merger ────────────────────────────────────────────────────────

export function cn(...inputs: (string | undefined | null | false)[]) {
  return inputs.filter(Boolean).join(' ')
}

// ── PocketBase date normalization ────────────────────────────────────────────
// PB 0.36 returns dates like "2026-03-29 10:15:30.123Z" (space instead of T).
// Some browsers reject that format. Normalize to ISO 8601 before parsing.
function pbDate(date: string | Date): Date {
  return new Date(typeof date === 'string' ? date.replace(' ', 'T') : date)
}

// ── Relative time ────────────────────────────────────────────────────────────

export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '\u2014'
  try {
    const d = pbDate(date)
    if (isNaN(d.getTime())) return '\u2014'

    const now = new Date()
    const diffSec = differenceInSeconds(now, d)
    const diffMin = differenceInMinutes(now, d)
    const diffHrs = differenceInHours(now, d)
    const diffDayCount = differenceInDays(now, d)

    if (diffSec < 10) return 'just now'
    if (diffSec < 60) return `${diffSec}s ago`
    if (diffMin < 60) return `${diffMin} min ago`
    if (diffHrs < 24) return `${diffHrs} hour${diffHrs !== 1 ? 's' : ''} ago`
    if (diffDayCount === 1) return 'yesterday'
    if (diffDayCount < 7) return `${diffDayCount} days ago`
    return format(d, 'MMM d')
  } catch {
    return '\u2014'
  }
}

// ── Format date ──────────────────────────────────────────────────────────────

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '\u2014'
  try {
    const d = pbDate(date)
    if (isNaN(d.getTime())) return '\u2014'
    return format(d, 'MMM d, yyyy')
  } catch {
    return '\u2014'
  }
}

// ── Format duration (ms -> human) ────────────────────────────────────────────

export function formatDuration(ms: number): string {
  if (!ms || ms < 0) return '\u2014'
  const totalSec = Math.floor(ms / 1000)
  const hours = Math.floor(totalSec / 3600)
  const min = Math.floor((totalSec % 3600) / 60)
  const sec = totalSec % 60

  if (hours > 0) return `${hours}h ${min.toString().padStart(2, '0')}m`
  if (min > 0) return `${min}m ${sec.toString().padStart(2, '0')}s`
  return `${sec}s`
}

// ── Elapsed time formatting (from start date to now) ─────────────────────────

export function formatElapsed(startDate: string | Date | null | undefined): string {
  if (!startDate) return '0s'
  try {
    const start = pbDate(startDate)
    if (isNaN(start.getTime())) return '0s'
    const totalSec = Math.max(0, differenceInSeconds(new Date(), start))
    const min = Math.floor(totalSec / 60)
    const sec = totalSec % 60
    if (min === 0) return `${sec}s`
    return `${min}m ${sec.toString().padStart(2, '0')}s`
  } catch {
    return '0s'
  }
}

// ── Status helpers ───────────────────────────────────────────────────────────

export function tenantStatusVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    active: 'success',
    provisioning: 'info',
    suspended: 'error',
    provision_failed: 'error',
    dormant: 'default',
    archived: 'default',
  }
  return map[status] ?? 'default'
}

export function jobStatusVariant(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' | 'brand' {
  const map: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info' | 'brand'> = {
    queued: 'default',
    running: 'info',
    complete: 'success',
    failed: 'error',
    cancelled: 'default',
  }
  return map[status] ?? 'default'
}

export function healthStatusColor(status: string): string {
  const map: Record<string, string> = {
    healthy: 'var(--status-healthy)',
    degraded: 'var(--status-warning)',
    unhealthy: 'var(--status-unhealthy)',
    unreachable: 'var(--status-unknown)',
  }
  return map[status] ?? 'var(--text-tertiary)'
}

// ── Phone formatting ────────────────────────────────────────────────────────

export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '---'
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  return phone // Return as-is if not a standard US number
}

// ── Slug generator ───────────────────────────────────────────────────────────

export function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 30)
}
